import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";
import { trackPurchase } from "@/lib/metaPixel";
import { trackPayment } from "@/lib/gtm";

export default function PaymentCallback() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "verifying", message: "" });
  // The extra-event-slot add-on path shows a dedicated submission popup
  // because the backend auto-creates the pending event server-side.
  const [extraSlotDone, setExtraSlotDone] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const reference = params.get("reference") || params.get("trxref");
    if (!reference) {
      setState({ status: "error", message: "Missing payment reference." });
      return;
    }
    api.get(`/subscription/verify/${encodeURIComponent(reference)}`)
      .then(async ({ data }) => {
        if (data.status !== "success") {
          setState({ status: "error", message: "Payment was not successful. You have not been charged." });
          return;
        }
        trackPurchase({ currency: "ZAR", content_name: data.add_on || data.plan_id || "subscription" });
        trackPayment({ redirect: data.redirect, reference, plan: data.add_on || data.plan_id, flow: "subscription" });
        const fresh = await refresh();

        if (data.add_on === "extra_event_slot") {
          // Backend already created the pending event (or there was no
          // pending payload — rare, but still show the popup so the creator
          // knows the slot has been added).
          setExtraSlotDone(true);
          setState({ status: "ok", message: "" });
          return;
        }

        setState({ status: "ok", message: "" });
        setTimeout(() => {
          // Prefer the backend-provided role-based redirect, fall back to
          // dashboardFor so the user always lands on the right portal.
          if (data.redirect) {
            window.location.href = data.redirect;
            return;
          }
          window.location.href = dashboardFor(fresh || { role: "parent", subscription_status: "active" });
        }, 1200);
      })
      .catch((e) => setState({ status: "error", message: errMsg(e, "Could not verify payment") }));
  }, [params, refresh]);

  const goToPortal = () => { window.location.href = "/event-creator-portal"; };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 grain" data-testid="payment-callback">
      <Dialog open={extraSlotDone} onOpenChange={(o) => { if (!o) goToPortal(); }}>
        <DialogContent className="max-w-md" data-testid="extra-slot-submitted-dialog">
          <DialogHeader>
            <div className="mx-auto mb-3 inline-flex w-14 h-14 rounded-full bg-sage-light items-center justify-center">
              <Mail className="w-7 h-7 text-sage-dark" />
            </div>
            <DialogTitle className="text-center font-heading font-black text-forest text-xl">
              Payment received — event submitted
            </DialogTitle>
            <DialogDescription className="text-center text-forest/70 pt-2" data-testid="extra-slot-submitted-msg">
              Thanks! Your payment is complete and your event has been submitted for approval.
              It will appear on your dashboard as <strong>Pending review</strong> until an admin approves it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full rounded-full bg-sage hover:bg-sage-dark text-white" onClick={goToPortal} data-testid="extra-slot-submitted-ok-btn">
              Back to dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 max-w-md w-full text-center shadow-sm">
        {state.status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-sage animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-black text-forest mb-2">Confirming your payment...</h1>
            <p className="text-forest/70 text-sm">Hang tight — this only takes a moment.</p>
          </>
        )}
        {state.status === "ok" && !extraSlotDone && (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-sage-light items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-sage-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">You're in!</h1>
            <p className="text-forest/70 text-sm" data-testid="payment-success-msg">Redirecting to your dashboard...</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-terracotta-light items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-terracotta-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">Payment could not be verified</h1>
            <p className="text-forest/70 text-sm mb-6" data-testid="payment-error-msg">{state.message}</p>
            <Button className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => navigate("/subscribe")} data-testid="payment-retry-btn">Back to plans</Button>
          </>
        )}
      </div>
    </div>
  );
}
